import appearanceChannel from 'util/appearance_channel'

import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { CallManager } from '../../components/calls/call_manager'
import ErrorBoundary from '../../containers/error_boundary/error_boundary'
import { AppAudioContext } from '../../contexts/app_audio_context'
import { AxiosContext } from '../../contexts/axios_context'
import { DevicesContext } from '../../contexts/devices_context'
import { ThemeContext } from '../../contexts/theme_context'
import { useDevicesManager } from '../../hooks/devices/use_devices_manager'
import { useAudioManager } from '../../hooks/use_audio_manager'
import { useDetectTheme } from '../../hooks/use_detect_theme'
import {
  getCurrentEnvironment,
  getCurrentMemberId,
} from '../../reduxes/slices/current/selectors'
import { useSelectorDelegateStore } from '../../reduxes/stores/root_store/hooks'
import { AppLocationProvider } from '../admin/app_location_provider'
import { LAZY_WIDGET_MEMBER_ID } from './constants'
import { Member } from './member'

type Props = {
  actionCableConsumer: any
  connectCablecarInstance: any
  rootStore: any
  subStore: any
}

const MemoizedCallManager = memo(CallManager)

export const LiveChatApp = ({
  actionCableConsumer,
  connectCablecarInstance,
  rootStore,
  subStore,
}: Props) => {
  const [cableCarSetup, setCableCarSetup] = useState(false)
  const [dispatches, setDispatches] = useState<{ [id: string]: any }>(() => {
    const mId = subStore.getState().current.memberId || LAZY_WIDGET_MEMBER_ID
    return mId
      ? {
          [mId]: subStore.dispatch,
        }
      : {}
  })
  const currentMemberId = useSelector(getCurrentMemberId)
  const axios = useContext(AxiosContext)
  const audioManagerValue = useAudioManager()
  const devicesManagerValue = useDevicesManager()
  const detectTheme = useDetectTheme()
  const environment = useSelectorDelegateStore(getCurrentEnvironment)
  const axiosInstances = useMemo(() => {
    return {
      [currentMemberId]: axios,
    }
  }, [axios, currentMemberId])

  useEffect(() => {
    if (!cableCarSetup && currentMemberId) {
      connectCablecarInstance()
      appearanceChannel(actionCableConsumer, {
        // eslint-disable-next-line
        // @ts-ignore
        ['live_chat_jwt']: localStorage.getItem(window.speakeasyData.jwtLsId),
      })
      setCableCarSetup(true)
    }
  }, [
    actionCableConsumer,
    cableCarSetup,
    connectCablecarInstance,
    environment,
    currentMemberId,
  ])

  useEffect(() => {
    if (currentMemberId) {
      setDispatches({ [currentMemberId]: subStore.dispatch })
    }
  }, [currentMemberId, subStore])

  return (
    <AppLocationProvider dispatches={dispatches}>
      <AppAudioContext.Provider value={audioManagerValue}>
        <DevicesContext.Provider value={devicesManagerValue}>
          <MemoizedCallManager
            dispatches={dispatches}
            axiosInstances={axiosInstances}
          >
            <ThemeContext.Provider value={detectTheme}>
              <ErrorBoundary liveChatVersion={true}>
                <Member rootStore={rootStore} subStore={subStore} />
              </ErrorBoundary>
            </ThemeContext.Provider>
          </MemoizedCallManager>
        </DevicesContext.Provider>
      </AppAudioContext.Provider>
    </AppLocationProvider>
  )
}
